// Flight lookup via the Cloudflare worker, which proxies AeroDataBox (RapidAPI).
// The RapidAPI key stays in Cloudflare Secrets; the worker verifies the Firebase
// token. A flight number repeats daily, so the date is required.
import firebase from "firebase/app";
import "firebase/auth";

const PROXY =
  process.env.REACT_APP_PROXY_URL ?? "https://intervals-proxy.sebastiangranlund.workers.dev";

export interface FlightAirport {
  name: string;
  iata?: string;
  lat: number | null;
  lng: number | null;
  timeLocal?: string; // "HH:MM" local
}
export interface FlightLookupResult {
  number: string;
  airline?: string;
  date?: string; // departure local date, YYYY-MM-DD
  departure: FlightAirport;
  arrival: FlightAirport;
}

const num = (v: any): number | null => {
  const n = typeof v === "number" ? v : parseFloat(v);
  return isNaN(n) ? null : n;
};
const hhmm = (s: any): string | undefined => {
  const m = /(\d{1,2}):(\d{2})/.exec(String(s || ""));
  return m ? `${m[1].padStart(2, "0")}:${m[2]}` : undefined;
};
const dateOnly = (s: any): string | undefined => {
  const m = /(\d{4}-\d{2}-\d{2})/.exec(String(s || ""));
  return m ? m[1] : undefined;
};

const parseFlight = (f: any, fallbackNumber: string): FlightLookupResult => ({
  number: String(f.number ?? fallbackNumber),
  airline: f.airline?.name,
  date: dateOnly(f.departure?.scheduledTime?.local ?? f.departure?.revisedTime?.local),
  departure: parseSide(f.departure),
  arrival: parseSide(f.arrival),
});

const toList = (data: any): any[] =>
  (Array.isArray(data) ? data : data?.departures ?? data?.flights ?? []).filter((f: any) => f && f.departure);

const parseSide = (side: any): FlightAirport => {
  const a = side?.airport ?? {};
  const loc = a.location ?? {};
  // Keep the airport name short — first two words only (e.g. "Stockholm Arlanda
  // Airport" → "Stockholm Arlanda") — then append the IATA code.
  const shortName = String(a.name || a.shortName || a.municipalityName || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .join(" ");
  const name = [shortName, a.iata ? `(${a.iata})` : ""].filter(Boolean).join(" ").trim();
  return {
    name,
    iata: a.iata,
    lat: num(loc.lat ?? loc.latitude),
    lng: num(loc.lon ?? loc.lng ?? loc.longitude),
    timeLocal: hhmm(side?.scheduledTime?.local ?? side?.revisedTime?.local),
  };
};

// In-memory caches (per session) so repeated lookups of the same flight don't
// re-hit the API. Only successful (non-empty) results are cached, so a flight
// that isn't in the data yet can still be retried later.
const datedCache = new Map<string, FlightLookupResult>();
const anyCache = new Map<string, FlightLookupResult[]>();

export const lookupFlight = async (
  number: string,
  dateISO: string
): Promise<FlightLookupResult | null> => {
  const user = firebase.auth().currentUser;
  if (!user) throw new Error("Inte inloggad.");
  const token = await user.getIdToken();
  const clean = number.replace(/\s+/g, "");
  const cacheKey = `${clean}|${dateISO}`;
  if (datedCache.has(cacheKey)) return datedCache.get(cacheKey) as FlightLookupResult;

  const res = await fetch(`${PROXY}/flight/${encodeURIComponent(clean)}/${dateISO}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 503) throw new Error("Flyguppslag är inte konfigurerat (API-nyckel saknas).");
  if (!res.ok) {
    let detail = `${res.status}`;
    try {
      const j = await res.json();
      if (j?.message) detail = `${res.status}: ${j.message}`;
    } catch { /* ignore */ }
    if (res.status === 404) return null;
    throw new Error(`Kunde inte hämta flyg (${detail}).`);
  }

  const data = await res.json();
  const f = toList(data)[0];
  if (!f) return null;
  const result = parseFlight(f, clean);
  datedCache.set(cacheKey, result);
  return result;
};

// Look up a flight by number only (no date): returns its known/upcoming
// occurrences (each with a date), so the caller can pick the one in trip range.
export const lookupFlightAny = async (number: string): Promise<FlightLookupResult[]> => {
  const user = firebase.auth().currentUser;
  if (!user) throw new Error("Inte inloggad.");
  const token = await user.getIdToken();
  const clean = number.replace(/\s+/g, "");
  if (anyCache.has(clean)) return anyCache.get(clean) as FlightLookupResult[];

  const res = await fetch(`${PROXY}/flight/${encodeURIComponent(clean)}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 503) throw new Error("Flyguppslag är inte konfigurerat (API-nyckel saknas).");
  if (!res.ok) {
    if (res.status === 404) return [];
    let detail = `${res.status}`;
    try { const j = await res.json(); if (j?.message) detail = `${res.status}: ${j.message}`; } catch { /* ignore */ }
    throw new Error(`Kunde inte hämta flyg (${detail}).`);
  }
  const data = await res.json();
  const results = toList(data).map((f) => parseFlight(f, clean));
  if (results.length > 0) anyCache.set(clean, results);
  return results;
};
